import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { accountErrorKey } from './account-form-model';
import { cariTypeLabel } from './cari-type-display';

export interface MoveCariTarget {
  id: string;
  companyName: string;
  source: 'customer' | 'partner';
}

interface MoveCariTypeDialogProps {
  /** The cari to move; null keeps the dialog closed. */
  cari: MoveCariTarget | null;
  /** The type the cari currently belongs to (excluded from targets). */
  currentTypeId: string;
  onOpenChange: (open: boolean) => void;
  /** Fired after a successful move so the caller can invalidate queries. */
  onMoved: () => void;
}

// Move within the same record kind: customers and partners remain separate.
// Moves over posted history come back as CONFIRM_REQUIRED and are retried
// with confirmed=true after an explicit acknowledgement.
export function MoveCariTypeDialog({
  cari,
  currentTypeId,
  onOpenChange,
  onMoved,
}: MoveCariTypeDialogProps) {
  const { t, i18n } = useTranslation('travelCari');
  const { t: tCommon } = useTranslation('common');
  const [targetTypeId, setTargetTypeId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setTargetTypeId('');
    setConfirmOpen(false);
    setSaveError(null);
  }, [cari?.id, !!cari]);

  const {
    data: types,
    isLoading,
    error,
    refetch,
  } = trpc.travelCariType.list.useQuery(undefined, { enabled: !!cari });

  const targetOptions: ComboboxOption[] = (types ?? [])
    .filter((tp) => tp.source === cari?.source && tp.id !== currentTypeId)
    .map((tp) => ({
      value: tp.id,
      label: `${tp.code} · ${cariTypeLabel(tp, i18n.language)}`,
      keywords: [tp.code, tp.name],
    }));

  const moveMutation = trpc.travelCariType.moveCari.useMutation({
    onSuccess: (result) => {
      showSuccessToast(
        result.accountCleared ? t('cariTypes.cariMovedAccountCleared') : t('cariTypes.cariMoved')
      );
      setConfirmOpen(false);
      onOpenChange(false);
      onMoved();
    },
    onError: (error) => {
      if (error.message.startsWith('CONFIRM_REQUIRED')) {
        setConfirmOpen(true);
      } else {
        const key = accountErrorKey(error);
        setSaveError(
          t(`account.errors.${key === 'saveFailed' || key === 'review' ? 'move' : key}`)
        );
      }
    },
  });

  const move = (confirmed: boolean) => {
    if (!cari || !targetTypeId || moveMutation.isPending) return;
    setSaveError(null);
    return moveMutation.mutateAsync({
      source: cari.source,
      cariId: cari.id,
      targetTypeId,
      confirmed,
    });
  };

  return (
    <>
      <Dialog
        open={!!cari}
        onOpenChange={(open) => {
          if (!moveMutation.isPending) onOpenChange(open);
        }}
      >
        <DialogContent className="max-w-md" closeDisabled={moveMutation.isPending}>
          <DialogHeader>
            <DialogTitle>{t('cariTypes.moveCari')}</DialogTitle>
            <DialogDescription>
              {t('cariTypes.moveCariDesc', { name: cari?.companyName ?? '' })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {isLoading && (
              <p role="status" className="text-sm">
                {t('account.loading')}
              </p>
            )}
            {error && (
              <div role="alert" className="space-y-2 text-sm">
                <p>{t('account.loadFailed')}</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    void refetch();
                  }}
                >
                  {t('account.retry')}
                </Button>
              </div>
            )}
            {saveError && (
              <p role="alert" className="text-destructive text-sm">
                {saveError}
              </p>
            )}
            <Combobox
              ariaLabel={t('directory.type')}
              disabled={isLoading || !!error || moveMutation.isPending}
              options={targetOptions}
              value={targetTypeId}
              onValueChange={(value) => {
                setTargetTypeId(value);
                setSaveError(null);
              }}
              placeholder={t('cariTypes.selectTargetType')}
              searchPlaceholder={t('cariTypes.searchAccount')}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                disabled={moveMutation.isPending}
                onClick={() => onOpenChange(false)}
              >
                {tCommon('cancel')}
              </Button>
              <Button
                disabled={!targetTypeId || isLoading || !!error || moveMutation.isPending}
                onClick={() => {
                  void move(false)?.catch(() => {});
                }}
              >
                {moveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('cariTypes.moveAction')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Posted history / incompatible account assignment acknowledgement */}
      <ConfirmationDialog
        open={confirmOpen}
        error={saveError}
        onOpenChange={setConfirmOpen}
        title={t('cariTypes.confirmMoveTitle')}
        description={t('cariTypes.confirmMove')}
        confirmLabel={t('cariTypes.moveAction')}
        onConfirm={async () => {
          await move(true);
        }}
        isLoading={moveMutation.isPending}
      />
    </>
  );
}
